'use client'

import { type ComponentProps, memo } from 'react'
import { defaultRehypePlugins, Streamdown, type StreamdownProps } from 'streamdown'

import { cn } from '@/lib/utils'

type Components = NonNullable<StreamdownProps['components']>
type ResponseProps = Omit<ComponentProps<typeof Streamdown>, 'components'> & {
  isStreaming?: boolean
  // Shallow-merged over the default ComponentsRendering map below — lets a caller override e.g.
  // just `a` (see widget-explain-report.tsx's citation-popover links) without losing the rest of
  // the default heading/list/table styling. Omitted by default (existing callers unaffected).
  components?: Partial<Components>
}

// Keep sanitize before raw so sanitization runs on the final HTML
const rehypePlugins = [defaultRehypePlugins.sanitize, defaultRehypePlugins.raw]

export const Response = memo(
  ({ className, isStreaming = false, components, ...props }: ResponseProps) => (
    <Streamdown
      className={cn(
        'size-full [&_code]:wrap-break-word [&_code]:whitespace-pre-wrap [&_pre]:max-w-full [&_pre]:overflow-x-auto [&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
        className
      )}
      caret="block"
      components={components ? { ...ComponentsRendering, ...components } : ComponentsRendering}
      controls={{ table: false }}
      // Defaults are duration=150ms/stagger=40ms per word — noticeably laggy on longer
      // responses. Cut both down so the reveal still reads as "typing" without lagging behind.
      animated={{ duration: 60, stagger: 10 }}
      mode={isStreaming ? 'streaming' : 'static'}
      isAnimating={isStreaming}
      rehypePlugins={rehypePlugins}
      parseIncompleteMarkdown={isStreaming}
      {...props}
    />
  ),
  (prevProps, nextProps) =>
    prevProps.children === nextProps.children &&
    prevProps.isStreaming === nextProps.isStreaming &&
    prevProps.components === nextProps.components
)

Response.displayName = 'Response'

const ComponentsRendering: Components = {
  h1: ({ children }) => (
    <h1 className="text-2xl font-bold tracking-tight text-white">{children}</h1>
  ),
  h2: ({ children }) => <h2 className="text-xl font-bold tracking-tight text-white">{children}</h2>,
  h3: ({ children }) => <h3 className="text-lg font-bold tracking-tight text-white">{children}</h3>,
  p: ({ children }) => <p className="text-sm tracking-tight text-white/80">{children}</p>,
  ul: ({ children }) => (
    <ul className="list-inside list-disc leading-relaxed tracking-tight text-white/80">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="list-inside list-decimal leading-relaxed tracking-tight text-white/80">
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li className="list-item leading-relaxed tracking-tight text-white/80">{children}</li>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline underline-offset-3 transition-opacity hover:opacity-80"
    >
      {children}
    </a>
  ),

  table: ({ children }) => (
    <div className="max-w-full overflow-x-auto">
      <table className="w-full min-w-max border-collapse text-sm">{children}</table>
    </div>
  ),

  // replace original HTML tags with custom components
  div: ({ children }) => <div className="text-sm tracking-tight text-white/80">{children}</div>,
  button: ({ children }) => (
    <button className="text-sm tracking-tight text-white/80">{children}</button>
  ),
}
